import { Matrix4, Vector2 } from 'three';

/**
 * 两条线段是否相交
 * @param {Vector2} p1 线段 p1p2 的一个端点 p1
 * @param {Vector2} p2 线段 p1p2 的一个端点 p2
 * @param {Vector2} q1 线段 q1q2 的一个端点 q1
 * @param {Vector2} q2 线段 q1q2 的一个端点 q2
 * @returns {boolean}
 */
const isLineSegmentsIntersect = (p1: Vector2, p2: Vector2, q1: Vector2, q2: Vector2) => {
  // 点 p1 到点 p2 的方向向量
  const r = p2.clone().sub(p1);

  // 点 q1 到点 q2 的方向向量
  const s = q2.clone().sub(q1);
  const denominator = r.cross(s);

  // 线段平行
  if (denominator === 0) return false;

  // 从点 p1 指向点 q1 的向量
  const p1q1 = new Vector2(q1.x - p1.x, q1.y - p1.y);

  // 交点相对于两个线段起点的位置
  const t = p1q1.cross(s) / denominator;
  const u = p1q1.cross(r) / denominator;

  // 交点位于两个线段上
  return 0 <= t && t <= 1 && 0 <= u && u <= 1;
};

/**
 * 线段是否与折线相交
 * @param vertex 折线包含的点集
 * @param q1 线段 q1q2 的端点 q1
 * @param q2 线段 q1q2 的端点 q2
 * @returns {boolean}
 */
export const isLinesIntersectLine = (vertex: Vector2[], q1: Vector2, q2: Vector2) => {
  return vertex.some((point, i) => {
    const next = i + 1;
    return isLineSegmentsIntersect(point, vertex[next > vertex.length - 1 ? 0 : next], q1, q2);
  });
};

export const isInImage = (width: number, height: number) => (points: number[]) => {
  return points[0] > 0 && points[1] > 0 && points[0] < width && points[1] < height;
};

/**
 * 判断两个二维向量是否近似相等
 * @param p1
 * @param p2
 * @param threshold 相等阈值，各维差值均小于该值则认为相等
 * @returns
 */
export const isSameVector2 = (p1: { x: number; y: number }, p2: { x: number; y: number }, threshold: number) =>
  Math.abs(p1.x - p2.x) < threshold && Math.abs(p1.y - p2.y) < threshold;

/**
 * 判断两个三维向量是否近似相等
 * @param p1
 * @param p2
 * @param threshold 相等阈值，各维差值均小于该值则认为相等
 * @returns
 */
export const isSameVector3 = (
  p1: { x: number; y: number; z: number },
  p2: { x: number; y: number; z: number },
  threshold: number,
) => Math.abs(p1.x - p2.x) < threshold && Math.abs(p1.y - p2.y) < threshold && Math.abs(p1.z - p2.z) < threshold;

const IDENTITY_MATRIX = new Matrix4();
export const isIdentityMatrix4 = (mat: Matrix4) => mat.equals(IDENTITY_MATRIX);

/**
 * 判断是否无坐标（无法在点云中显示）
 * @param data
 * @returns {boolean}
 */
export const isNoCoordinate = (data: number[]): boolean =>
  data.length === 0 ||
  data.some(
    (val) =>
      !((typeof val === 'number' && !isNaN(val)) || (typeof val === 'string' && val !== '' && !isNaN(Number(val)))),
  );
